import React, { useEffect, useState } from 'react';

import * as Yup from 'yup';
import { Link } from '@mui/material';
import { Alert } from '@mui/material';
import cn from 'classnames';
import { Form, Formik, FormikHelpers } from 'formik';
import NextLink from 'next/link';

import { CodedErrorMessage, DjangoError } from '@api/ApiTypes';

import GoogleIdentityButton from '@components/auth/GoogleIdentityButton';
import Button from '@components/Button';
import { FormikFieldError } from '@components/forms/FieldError';
import { Schemas } from '@components/forms/Forms';
import TextInput from '@components/forms/TextInput';
import TextInputField from '@components/forms/TextInputField';
import HorizontalLine from '@components/HorizontalLine';
import LoadingButton from '@components/LoadingButton';

import useExtractedEmailFromURL from '@hooks/extractEmailFromURL';
import useAuth from '@hooks/useAuth';
// Update the path accordingly
import useInviteHref from '@hooks/useInviteHref';
import useInviteValidation from '@hooks/useInviteValidation';

import { fireRegistrationEvent } from '@services/AnalyticsService';
import { ApiError } from '@services/ApiError';

import Logger from '@util/Logger';

const logger = Logger.make('SignupForm');

const blockedDomains = ['bob.com'];

const SignupSchema = Yup.object({
    email: Schemas.email.test(
        'Blocked Domain',
        "Domain not allowed, please enter your organization's email address.",
        (value) => {
            if (!value) return false;
            const domain = value.split('@')[1];
            return !blockedDomains.includes(domain);
        },
    ),
    password: Schemas.password,
    // passwordConfirm: Schemas.passwordConfirm,
    terms_accepted: Yup.bool().required().oneOf([true], 'Please accept the Terms of Use and Privacy Policy below'),
});

type FormValues = Yup.InferType<typeof SignupSchema>;
type Props = { loginLinkText?: string; loginHref?: string };
const SignupForm = ({ loginLinkText = 'Log in', loginHref = 'login' }: Props) => {
    const [showPassword, setShowPassword] = useState(false);
    const [authError, setAuthError] = useState<string | null>(null);
    const [showLoggedInMessage, setShowLoggedInMessage] = useState(true);
    const [needsRedirect, setNeedsRedirect] = useState(false);
    const { user, logout, submitSignup, loginSuccessRedirect } = useAuth();
    const isValidInvite = useInviteValidation();
    const fullLoginHref = useInviteHref(loginHref);

    const extractedEmail = useExtractedEmailFromURL() || '';
    const initialValues: FormValues = { email: extractedEmail, password: '', terms_accepted: false };

    useEffect(() => {
        if (needsRedirect && user) {
            logger.info('[useEffect] sending user to new page');
            loginSuccessRedirect(user);
        }
    }, [user, needsRedirect]);

    const submitForm = async (payload: FormValues, formik: FormikHelpers<FormValues>) => {
        setShowLoggedInMessage(false);
        try {
            setAuthError(null);
            setNeedsRedirect(true);
            const user = await submitSignup(payload);
            if (user) {
                fireRegistrationEvent(user, 'email');
            } else {
                formik.setSubmitting(false);
                setNeedsRedirect(false);
            }
        } catch (error) {
            setNeedsRedirect(false);
            logger.error(error);
            const knownErrors = (error as ApiError<DjangoError<{ email: CodedErrorMessage[] }>>)?.body?.details?.email;

            if (knownErrors) {
                setAuthError(knownErrors.map((e) => e.message).join('\n'));
            } else {
                setAuthError(ApiError.getMessage(error as Error));
            }

            setShowLoggedInMessage(true);
            formik.setSubmitting(false);
        }
    };

    if (user && showLoggedInMessage) {
        return (
            <div>
                <Alert
                    severity="success"
                    action={
                        <Button onClick={() => logout()} color="primary">
                            Log out
                        </Button>
                    }
                >
                    You are signed in
                </Alert>
            </div>
        );
    }

    if (isValidInvite) {
        return (
            <Formik initialValues={initialValues} onSubmit={submitForm} validationSchema={SignupSchema}>
                {({ errors, touched, values, handleChange, isSubmitting }) => (
                    <Form noValidate>
                        {authError && (
                            <Alert
                                severity="error"
                                onClose={() => setAuthError(null)}
                                className="mb-8"
                                data-cy="auth-error-alert"
                            >
                                {authError}
                            </Alert>
                        )}

                        <GoogleIdentityButton
                            signup={true}
                            terms_accepted={values.terms_accepted}
                            onConnectStarted={() => setNeedsRedirect(true)}
                            onCancel={() => setNeedsRedirect(false)}
                        />

                        <HorizontalLine text="or" className="my-10" />

                        <TextInputField
                            label="Email address"
                            name="email"
                            type="email"
                            autoComplete="email"
                            data-cy="email"
                        />

                        <TextInput
                            label="Password"
                            name="password"
                            type={showPassword ? 'text' : 'password'}
                            autoComplete="new-password"
                            required
                            data-cy="password"
                            placeholder=""
                            value={values.password}
                            onChange={handleChange}
                            useFormikError
                            labelRight={
                                <label className="inline-flex cursor-pointer items-center pl-1 text-sm">
                                    <input
                                        type="checkbox"
                                        className="rounded text-indigo-500"
                                        checked={showPassword}
                                        onChange={(e) => setShowPassword(e.target.checked)}
                                    />
                                    <span className="ml-2">Show password</span>
                                </label>
                            }
                        />

                        <div className="form-field">
                            <label
                                className={cn('mb-1 inline-flex cursor-pointer items-start pl-1', {
                                    'has-error': errors.terms_accepted && touched.terms_accepted,
                                })}
                            >
                                <input
                                    type="checkbox"
                                    className={cn('mt-1 rounded text-indigo-500', {
                                        error: errors.terms_accepted && touched.terms_accepted,
                                    })}
                                    name="terms_accepted"
                                    data-cy="terms"
                                    onChange={handleChange}
                                />
                                <span className="ml-2">
                                    By checking this box, I acknowledge that I accept the{' '}
                                    <NextLink
                                        href="https://pluto.bio/legal/terms"
                                        passHref
                                        target="_blank"
                                        className="whitespace-nowrap"
                                    >
                                        Terms of Use
                                    </NextLink>{' '}
                                    and{' '}
                                    <NextLink
                                        href="https://pluto.bio/privacy-policy"
                                        passHref
                                        target="_blank"
                                        className="whitespace-nowrap"
                                    >
                                        Privacy Policy
                                    </NextLink>
                                </span>
                            </label>
                            <FormikFieldError name="terms_accepted" className="pl-7" />
                        </div>
                        <div className="mb-4">
                            <LoadingButton
                                type="submit"
                                fullWidth
                                data-cy="submit"
                                loading={isSubmitting}
                                disabled={isSubmitting || needsRedirect}
                            >
                                Sign Up
                            </LoadingButton>
                        </div>

                        <div className="flex justify-center ">
                            <span className="mr-1 text-gray-500">{'Already have an account? '}</span>
                            <Link href={fullLoginHref} variant="body2">
                                {loginLinkText}
                            </Link>
                        </div>
                    </Form>
                )}
            </Formik>
        );
    } else {
        return (
            <div className="border p-6">
                Registration is only allowed for invited users. Please contact your Pluto administrator to invite you to
                a labspace.
            </div>
        );
    }
};

export default SignupForm;
